import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="modal"
// Can be used on the modal element itself, or on a trigger that targets a modal by ID
export default class extends Controller {
  static targets = ["modal", "title", "blockId", "date", "closeButton"]
  static values = {
    modalId: String
  }

  connect() {
    // If this controller is on the modal element itself, set up handlers
    if (this.hasModalTarget || this.element.classList.contains("schedule-modal")) {
      this.boundHandleOutsideClick = this.handleOutsideClick.bind(this)
      this.boundHandleKeydown = this.handleKeydown.bind(this)
      const modalElement = this.hasModalTarget ? this.modalTarget : this.element
      modalElement.addEventListener("click", this.boundHandleOutsideClick)
      modalElement.addEventListener("keydown", this.boundHandleKeydown)
    }
  }

  disconnect() {
    if (this.boundHandleOutsideClick) {
      const modalElement = this.hasModalTarget ? this.modalTarget : this.element
      modalElement.removeEventListener("click", this.boundHandleOutsideClick)
    }
    if (this.boundHandleKeydown) {
      const modalElement = this.hasModalTarget ? this.modalTarget : this.element
      modalElement.removeEventListener("keydown", this.boundHandleKeydown)
    }
  }

  show(event) {
    event.preventDefault()

    // Get the modal element - either from target, value, or this element
    let modalElement
    if (this.hasModalTarget) {
      modalElement = this.modalTarget
    } else if (this.hasModalIdValue) {
      modalElement = document.getElementById(this.modalIdValue)
    } else if (this.element.classList.contains("schedule-modal")) {
      modalElement = this.element
    } else {
      return
    }

    if (!modalElement) return

    // Get data from the trigger element
    const trigger = event.currentTarget
    const blockTitle = trigger.getAttribute("data-block-title")
    const blockId = trigger.getAttribute("data-block-id")
    const blockDate = trigger.getAttribute("data-block-date")

    // Find targets within the modal
    const titleElement = modalElement.querySelector("[data-modal-target='title']")
    const blockIdElement = modalElement.querySelector("[data-modal-target='blockId']")
    const dateElement = modalElement.querySelector("[data-modal-target='date']")

    // Populate modal fields if they exist
    if (blockTitle && titleElement) {
      titleElement.innerHTML = blockTitle
    }
    if (blockId && blockIdElement) {
      blockIdElement.value = blockId
    }
    if (blockDate && dateElement) {
      dateElement.value = blockDate
    }

    // Show the modal
    this.previouslyFocusedElement = document.activeElement
    modalElement._previouslyFocusedElement = document.activeElement
    modalElement.style.display = "block"
    modalElement.setAttribute("aria-hidden", "false")
    this.focusInitialElement(modalElement)
  }

  hide(event) {
    if (event) event.preventDefault()
    const modalElement = this.hasModalTarget ? this.modalTarget : this.element
    if (modalElement) {
      modalElement.style.display = "none"
      modalElement.setAttribute("aria-hidden", "true")
      this.restoreFocus(modalElement)
    }
  }

  handleOutsideClick(event) {
    // Close modal if clicking on the backdrop (the modal element itself, not its children)
    const modalElement = this.hasModalTarget ? this.modalTarget : this.element
    if (event.target === modalElement) {
      this.hide(event)
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.hide(event)
    }
  }

  focusInitialElement(modalElement) {
    const closeButton = modalElement.querySelector("[data-modal-target='closeButton']")
    const focusableSelector = "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
    const firstFocusable = modalElement.querySelector(focusableSelector)
    const focusTarget = closeButton || firstFocusable
    if (focusTarget) focusTarget.focus()
  }

  restoreFocus(modalElement) {
    const previous = this.previouslyFocusedElement || modalElement?._previouslyFocusedElement
    if (previous && previous.focus) {
      previous.focus()
    }
    this.previouslyFocusedElement = null
    if (modalElement) modalElement._previouslyFocusedElement = null
  }
}
